import React, { FC, useMemo } from 'react';
import { SocialConsentModal, SocialConsentModalProps } from '@minecraft.organisms';
import { Trans, useTranslation, TranslateFn } from '@blocs.i18n';
import { appConfig as globalAppConfig } from '@minecraft.app-config';
import { PaywallLocation, useBillingPopup } from '@blocs.paywall';

const PrivacyPolicyLink: FC = ({ children }) => (
  <a href="https://www.castingnetworks.com/privacy" target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

// Enum for consent/paywall modal types
export enum ConsentType {
  InstagramConsent = 'instagram-consent',
  YoutubeConsent = 'youtube-consent',
  InstagramPaywall = 'instagram-paywall',
  MultiPaywall = 'multi-paywall',
}

const getFeatures = (type: ConsentType): { key: string; content: React.ReactNode }[] => {
  switch (type) {
    case ConsentType.InstagramConsent:
    case ConsentType.InstagramPaywall:
      return [
        {
          key: 'instagram-feature-1',
          content: <Trans i18nKey="common:profileEdit.social.instagram.consentModal.feature1" />,
        },
        {
          key: 'instagram-feature-2',
          content: <Trans i18nKey="common:profileEdit.social.instagram.consentModal.feature2" />,
        },
        {
          key: 'instagram-feature-3',
          content: <Trans i18nKey="common:profileEdit.social.instagram.consentModal.feature3" />,
        },
      ];
    case ConsentType.YoutubeConsent:
      return [
        {
          key: 'youtube-feature-1',
          content: <Trans i18nKey="common:profileEdit.social.youtube.consentModal.feature1" />,
        },
        {
          key: 'youtube-feature-2',
          content: <Trans i18nKey="common:profileEdit.social.youtube.consentModal.feature2" />,
        },
        {
          key: 'youtube-feature-3',
          content: <Trans i18nKey="common:profileEdit.social.youtube.consentModal.feature3" />,
        },
      ];
    case ConsentType.MultiPaywall:
      return [
        {
          key: 'multi-paywall-feature-1',
          content: <Trans i18nKey="common:profileEdit.social.paywall.consentModal.feature1" />,
        },
        {
          key: 'multi-paywall-feature-2',
          content: <Trans i18nKey="common:profileEdit.social.paywall.consentModal.feature2" />,
        },
        {
          key: 'multi-paywall-feature-3',
          content: <Trans i18nKey="common:profileEdit.social.paywall.consentModal.feature3" />,
        },
      ];
    default:
      return [];
  }
};

const getFooterItems = (type: ConsentType, t: TranslateFn): SocialConsentModalProps['footerItems'] => {
  if (
    type === ConsentType.InstagramConsent ||
    type === ConsentType.InstagramPaywall ||
    type === ConsentType.MultiPaywall
  ) {
    return [
      {
        key: 'footer-item-1',
        icon: 'switch-accounts',
        title: t('common:profileEdit.social.consentModal.footer.itemHeader1'),
        description: t('common:profileEdit.social.consentModal.footer.itemDescription1'),
      },
      {
        key: 'footer-item-2',
        icon: 'lock',
        title: t('common:profileEdit.social.consentModal.footer.itemHeader2'),
        description: (
          <Trans
            i18nKey="common:profileEdit.social.consentModal.footer.itemDescription2"
            components={{ a: <PrivacyPolicyLink /> }}
          />
        ),
      },
    ];
  }
  if (type === ConsentType.YoutubeConsent) {
    return [
      {
        key: 'footer-item-1',
        icon: 'lock',
        title: t('common:profileEdit.social.consentModal.footer.itemHeader2'),
        description: (
          <Trans
            i18nKey="common:profileEdit.social.consentModal.footer.itemDescription2"
            components={{ a: <PrivacyPolicyLink /> }}
          />
        ),
      },
    ];
  }
  return [];
};

interface UnifiedConsentModalProps {
  close: () => void;
  consentType: ConsentType;
  'data-testid'?: string;
  onPrimaryAction?: () => void;
  mutationLoading?: boolean;
}

export const UnifiedConsentModal = ({
  close,
  consentType,
  'data-testid': dataTestId,
  onPrimaryAction,
  mutationLoading = false,
}: UnifiedConsentModalProps) => {
  const { t } = useTranslation();
  const billingPopup = useBillingPopup();

  // Button config
  const modalButtons: SocialConsentModalProps['buttonsConfig'] = useMemo(() => {
    if (consentType === ConsentType.InstagramPaywall || consentType === ConsentType.MultiPaywall) {
      return [
        {
          label: t('common:button.notNow'),
          onClick: close,
          variant: 'secondary',
        },
        {
          onClick: () => billingPopup.show({ location: PaywallLocation.SOCIAL_UPGRADE }),
          startIcon: { name: 'rocket', justify: 'end' },
          label: { name: t('common:membership.upgradeToPremiumShort'), justify: 'start' },
          color: 'hotTrialPaywall',
          fixedWidth: true,
        },
      ];
    }
    return [
      {
        label: t('common:button.notNow'),
        onClick: close,
        variant: 'secondary',
      },
      {
        label: t('common:profileEdit.social.consentModal.button.connectAndShare'),
        onClick: onPrimaryAction,
        disabled: mutationLoading,
      },
    ];
  }, [t, close, billingPopup, onPrimaryAction, mutationLoading, consentType]);

  const getPlatformIcon = (): string => {
    switch (consentType) {
      case ConsentType.InstagramConsent:
      case ConsentType.InstagramPaywall:
        return 'profile-social-instagram';
      case ConsentType.YoutubeConsent:
        return 'profile-social-youtube';
      case ConsentType.MultiPaywall:
        return null;
      default:
        return null;
    }
  };

  const getPreviewImage = (): SocialConsentModalProps['previewImage'] | undefined => {
    if (consentType === ConsentType.MultiPaywall)
      return {
        src: `${globalAppConfig.cdnRoot}/images/static/profile-social-youtube-instagram-preview.png`,
        srcSet: `${globalAppConfig.cdnRoot}/images/static/profile-social-youtube-instagram-preview-4x.png`,
        alt: t('common:profileEdit.social.consentModal.previewImageAlt'),
      };
    if (consentType === ConsentType.YoutubeConsent) {
      return {
        src: `${globalAppConfig.cdnRoot}/images/static/profile-social-youtube-preview.png`,
        srcSet: `${globalAppConfig.cdnRoot}/images/static/profile-social-youtube-preview-4x.png`,
        alt: t('common:profileEdit.social.consentModal.previewImageAlt'),
      };
    }
    return {
      src: `${globalAppConfig.cdnRoot}/images/static/profile-social-preview.png`,
      srcSet: `${globalAppConfig.cdnRoot}/images/static/profile-social-preview-4x.png`,
      alt: t('common:profileEdit.social.consentModal.previewImageAlt'),
    };
  };

  const previewImage = getPreviewImage();
  const platformIcon = getPlatformIcon();

  // Titles and headers
  const titleMap: Record<ConsentType, string> = {
    [ConsentType.InstagramConsent]: t('common:profileEdit.social.consentModal.title.premiumUser', {
      socialMediaPlatform: 'Instagram',
    }),
    [ConsentType.YoutubeConsent]: t('common:profileEdit.social.consentModal.title.premiumUser', {
      socialMediaPlatform: 'YouTube',
    }),
    [ConsentType.InstagramPaywall]: t('common:profileEdit.social.consentModal.title', {
      socialMediaPlatform: 'Instagram',
    }),
    [ConsentType.MultiPaywall]: t('common:profileEdit.social.consentModal.title', {
      socialMediaPlatform: 'Social Links',
    }),
  };
  const featureHeaderMap: Record<ConsentType, string> = {
    [ConsentType.InstagramConsent]: t('common:profileEdit.social.consentModal.featureHeader.premiumUser'),
    [ConsentType.YoutubeConsent]: t('common:profileEdit.social.consentModal.featureHeader.premiumUser'),
    [ConsentType.InstagramPaywall]: t('common:profileEdit.social.consentModal.featureHeader'),
    [ConsentType.MultiPaywall]: t('common:profileEdit.social.consentModal.featureHeader'),
  };

  // Features and footer
  const featureItems = useMemo(() => getFeatures(consentType), [consentType]);
  const footerItems = useMemo(() => getFooterItems(consentType, t), [consentType, t]);

  return (
    <SocialConsentModal
      isOpen
      close={close}
      buttonsConfig={modalButtons}
      description={t('common:profileEdit.social.consentModal.description')}
      platformIcon={platformIcon}
      previewImage={previewImage}
      title={titleMap[consentType]}
      data-testid={dataTestId}
      footerItems={footerItems}
      featureHeader={featureHeaderMap[consentType]}
      features={featureItems}
    />
  );
};
